import React from 'react';
import { Col, Form, Row } from 'components';
import { FormModal } from 'components/common';
import { useEditPricing } from './useEditPricing';
import { InputNumber, Tag, Typography } from 'antd';
import { preventNonNumericValue } from 'utils/helpers';
import { PercentageIcon } from 'components/common/icons';
import styles from './../../styles.module.scss';

const { Text } = Typography;
const { Item } = Form;

export type SupplierPricingRuleType = {
  id: string;
  markup: number;
  minMargin: number;
  maxMargin: number;
  minLeadTime: number;
  maxLeadTime: number;
  discount: number;
  type: string;
};

export interface IEditPricing {
  modalState: boolean;
  onModalStateChange: () => void;
  dataToEdit?: SupplierPricingRuleType;
}

export const EditPricing = React.memo((props: IEditPricing) => {
  const { onModalStateChange, modalState, dataToEdit } = props;
  const { onFinish, disableSaveBtn, handleTypeChange, type, form, isLoading, clearFieldValidation, onCancel } =
    useEditPricing({
      onModalStateChange,
      modalState,
      dataToEdit,
    });
  return (
    <FormModal
      form={form}
      title={'Update Pricing Rules'}
      okText={'Save'}
      open={modalState}
      isLoading={isLoading}
      onCancel={onCancel}
      onSubmit={onFinish}
      disableOkBtn={disableSaveBtn}
    >
      <Row gutter={[16, 16]}>
        <Col span={12}>
          <Item
            label={'Margin'}
            name={'markup'}
            rules={[
              {
                validator: (_, value) => {
                  if (value === undefined || value === null || value === '') {
                    return Promise.resolve();
                  }
                  const num = parseFloat(value);
                  if (isNaN(num)) {
                    return Promise.reject(new Error('Margin must be a valid number.'));
                  }
                  if (num < 0) {
                    return Promise.reject(new Error('Margin must be a positive number.'));
                  }
                  if (!/^\d+(\.\d{1,2})?$/.test(value)) {
                    return Promise.reject(new Error('Margin can have up to 2 decimal points.'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <InputNumber<number>
              controls={false}
              size={'large'}
              onBeforeInput={(event: any) => preventNonNumericValue(event)}
              style={{ width: '100%' }}
              suffix={<PercentageIcon />}
            />
          </Item>
        </Col>
        <Col span={6}>
          <Item
            label={'Margin'}
            name={'minMargin'}
            rules={[
              ({ getFieldValue }) => ({
                validator: async (_, value) => {
                  if (value === undefined || value === null || value === '') {
                    return Promise.resolve();
                  }
                  const num = parseFloat(value);
                  if (isNaN(num)) {
                    return Promise.reject(new Error('Min margin must be a valid number.'));
                  }
                  if (num <= 0) {
                    return Promise.reject(new Error('Min margin must be a positive number.'));
                  }
                  if (!/^\d+(\.\d{1,2})?$/.test(value)) {
                    return Promise.reject(new Error('Min margin can have up to 2 decimal points.'));
                  }
                  const maxMargin = getFieldValue('maxMargin');
                  if (maxMargin !== undefined && maxMargin !== null && num > maxMargin) {
                    return Promise.reject(new Error('Min margin cannot be greater than Max margin.'));
                  }
                  clearFieldValidation(form, 'maxMargin');
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <InputNumber<number>
              controls={false}
              size={'large'}
              onBeforeInput={(event: any) => preventNonNumericValue(event)}
              style={{ width: '100%' }}
              suffix={<PercentageIcon />}
              placeholder={'Min'}
            />
          </Item>
        </Col>
        <Col span={6}>
          <Item
            label={<span style={{ display: 'none' }}></span>}
            name={'maxMargin'}
            rules={[
              ({ getFieldValue }) => ({
                validator: async (_, value) => {
                  if (value === undefined || value === null || value === '') {
                    return Promise.resolve();
                  }
                  const num = parseFloat(value);
                  if (isNaN(num)) {
                    return Promise.reject(new Error('Max margin must be a valid number.'));
                  }
                  if (num <= 0) {
                    return Promise.reject(new Error('Max margin must be a positive number.'));
                  }
                  if (!/^\d+(\.\d{1,2})?$/.test(value)) {
                    return Promise.reject(new Error('Max margin can have up to 2 decimal points.'));
                  }

                  const minMargin = getFieldValue('minMargin');
                  if (minMargin !== undefined && num < minMargin) {
                    return Promise.reject(new Error('Max margin cannot be less than Min margin.'));
                  }
                  clearFieldValidation(form, 'minMargin');
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <InputNumber<number>
              controls={false}
              size={'large'}
              onBeforeInput={(event: any) => preventNonNumericValue(event)}
              style={{ width: '100%' }}
              suffix={<PercentageIcon />}
              placeholder={'Max'}
            />
          </Item>
        </Col>
        <Col span={12}>
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Item
                label={'Lead Time'}
                name={'minLeadTime'}
                rules={[
                  ({ getFieldValue }) => ({
                    validator: async (_, value) => {
                      if (value === undefined || value === null || value === '') {
                        return Promise.resolve();
                      }
                      if (!/^[1-9]\d*$/.test(String(value))) {
                        return Promise.reject(new Error('Minimum lead time must be a positive integer.'));
                      }
                      const maxLeadTime = getFieldValue('maxLeadTime');
                      if (maxLeadTime && value > maxLeadTime) {
                        return Promise.reject(new Error('Minimum lead time must be less than Maximum.'));
                      }
                      clearFieldValidation(form, 'maxLeadTime');
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <InputNumber<number>
                  controls={false}
                  size={'large'}
                  onBeforeInput={(event: any) => preventNonNumericValue(event)}
                  style={{ width: '100%' }}
                  placeholder={'Min'}
                />
              </Item>
            </Col>
            <Col span={12}>
              <Item
                label={<span style={{ display: 'none' }}></span>}
                name={'maxLeadTime'}
                rules={[
                  ({ getFieldValue }) => ({
                    validator: async (_, value) => {
                      // Allow empty values (optional field)
                      if (value === undefined || value === null || value === '') {
                        return Promise.resolve();
                      }
                      // Validate positive integer (e.g. "1", "2", "10", etc.)
                      if (!/^[1-9]\d*$/.test(String(value))) {
                        return Promise.reject(new Error('Maximum lead time must be a positive integer.'));
                      }
                      const minLeadTime = getFieldValue('minLeadTime');
                      if (minLeadTime !== undefined && value < minLeadTime) {
                        return Promise.reject(new Error('Maximum lead time must be more than Minimum.'));
                      }
                      clearFieldValidation(form, 'minLeadTime');
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <InputNumber<number>
                  controls={false}
                  size={'large'}
                  onBeforeInput={(event: any) => preventNonNumericValue(event)}
                  style={{ width: '100%' }}
                  suffix={
                    <Row className={'gap-4'}>
                      <Tag
                        className={`${styles.tag} ${type === 'DAY' && styles.selected_tag}`}
                        onClick={() => handleTypeChange('DAY')}
                      >
                        Day
                      </Tag>
                      <Tag
                        className={`${styles.tag} ${type === 'WEEK' && styles.selected_tag}`}
                        onClick={() => handleTypeChange('WEEK')}
                      >
                        Week
                      </Tag>
                    </Row>
                  }
                  placeholder={'Max'}
                />
              </Item>
            </Col>
          </Row>
        </Col>
        <Col span={12}>
          <Item
            label={'Discount'}
            name={'discount'}
            rules={[
              {
                validator: (_, value) => {
                  if (value === undefined || value === null || value === '') {
                    return Promise.resolve();
                  }
                  const num = parseFloat(value);

                  if (isNaN(num)) {
                    return Promise.reject(new Error('Discount must be a valid number.'));
                  }

                  if (num < 0) {
                    return Promise.reject(new Error('Discount must be a positive number.'));
                  }

                  if (num >= 100) {
                    return Promise.reject(new Error('Discount must be less than 100.'));
                  }

                  if (!/^\d+(\.\d{1,2})?$/.test(value)) {
                    return Promise.reject(new Error('Discount can have up to 2 decimal points.'));
                  }

                  return Promise.resolve();
                },
              },
            ]}
          >
            <InputNumber<number>
              controls={false}
              size={'large'}
              onBeforeInput={(event: any) => preventNonNumericValue(event)}
              style={{ width: '100%' }}
              suffix={<PercentageIcon />}
            />
          </Item>
        </Col>
        <Col span={12}>
          <Item
            label={'Minimum Order Fee'}
            name={'minimumOrderFee'}
            rules={[
              {
                validator: async (_: any, value: any) => {
                  if (value === undefined || value === null || value === '') {
                    return Promise.resolve();
                  }
                  if (!/^[1-9]\d*$/.test(String(value))) {
                    return Promise.reject(new Error('Minimum order fee must be a positive integer.'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <InputNumber<number>
              controls={false}
              size={'large'}
              onBeforeInput={(event: any) => preventNonNumericValue(event)}
              style={{ width: '100%' }}
              suffix={<Text type={'secondary'}>€</Text>}
            />
          </Item>
        </Col>
      </Row>
    </FormModal>
  );
});
